import React, { forwardRef, useState } from 'react';
import { createInvoiceFromOrder, updateInvoiceStatus } from '../../../../services/invoicesApi';
import styles from './GenerateInvoiceForm.module.css';

interface FormState {
  orderId: string;
  customer: string;
  shippingAddress: string;
  paymentTerms: string;
  creditLimit: string;
  invoiceDate: string;
  dueDate: string;
  referenceNumber: string;
  notes: string;
  description: string;
}

const INITIAL_FORM: FormState = {
  orderId: '',
  customer: '',
  shippingAddress: '',
  paymentTerms: '',
  creditLimit: '',
  invoiceDate: '',
  dueDate: '',
  referenceNumber: '',
  notes: '',
  description: '',
};

interface Props {
  onCreated?: () => void;
}

export const GenerateInvoiceForm = forwardRef<HTMLDivElement, Props>(({ onCreated }, ref) => {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState<'generate' | 'draft' | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const handleChange = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setMessage(null);
  };

  const submit = async (mode: 'generate' | 'draft') => {
    setMessage(null);
    const orderIdNum = parseInt(form.orderId.trim(), 10);
    if (!orderIdNum) {
      setMessage({ tone: 'err', text: 'Order ID is required' });
      return;
    }
    setSubmitting(mode);
    try {
      const invoice = await createInvoiceFromOrder(orderIdNum);
      if (mode === 'draft' && invoice.status !== 'draft') {
        await updateInvoiceStatus(invoice.id, 'draft', 'Saved as draft from generator');
      }
      setMessage({ tone: 'ok', text: `Created invoice ${invoice.invoice_number}` });
      handleReset();
      onCreated?.();
    } catch (err) {
      setMessage({ tone: 'err', text: err instanceof Error ? err.message : 'Failed to generate invoice' });
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className={styles.wrapper} ref={ref}>
      <div className={styles.formHeader}>
        <h2 className={styles.formTitle}>INVOICE</h2>
        <p className={styles.formSubtitle}>
          Enter a delivered Order ID to generate a billable invoice. Customer / shipping
          info is sourced from the order.
        </p>
      </div>

      <div className={styles.formGrid}>
        <div className={styles.leftCol}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>
              Order ID<span className={styles.required}>*</span>
            </label>
            <input
              className={styles.input}
              type="number"
              value={form.orderId}
              onChange={handleChange('orderId')}
              placeholder="e.g. 142"
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Customer (optional override)</label>
            <input
              className={styles.input}
              type="text"
              value={form.customer}
              onChange={handleChange('customer')}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Shipping Address</label>
            <input
              className={styles.input}
              type="text"
              value={form.shippingAddress}
              onChange={handleChange('shippingAddress')}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Payment Terms</label>
            <input
              className={styles.input}
              type="text"
              value={form.paymentTerms}
              onChange={handleChange('paymentTerms')}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Credit Limit</label>
            <input
              className={styles.input}
              type="text"
              value={form.creditLimit}
              onChange={handleChange('creditLimit')}
            />
          </div>
        </div>

        <div className={styles.rightCol}>
          <div className={styles.dateRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Invoice Date</label>
              <input
                className={styles.input}
                type="date"
                value={form.invoiceDate}
                onChange={handleChange('invoiceDate')}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Due Date</label>
              <input
                className={styles.input}
                type="date"
                value={form.dueDate}
                onChange={handleChange('dueDate')}
              />
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Reference Number</label>
            <input
              className={styles.input}
              type="text"
              value={form.referenceNumber}
              onChange={handleChange('referenceNumber')}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Notes</label>
            <textarea
              className={`${styles.input} ${styles.notesArea}`}
              value={form.notes}
              onChange={handleChange('notes')}
            />
          </div>
        </div>
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>Description</label>
        <textarea
          className={`${styles.input} ${styles.descArea}`}
          value={form.description}
          onChange={handleChange('description')}
        />
      </div>

      {message && (
        <p style={{
          color: message.tone === 'ok' ? '#059669' : '#DC2626',
          marginTop: 12, fontSize: 13,
        }}>
          {message.text}
        </p>
      )}

      <div className={styles.formFooter}>
        <button
          className={styles.generateBtn}
          onClick={() => submit('generate')}
          disabled={!!submitting}
        >
          {submitting === 'generate' ? 'Generating…' : 'Generate Invoice'}
        </button>
        <button
          className={styles.draftBtn}
          onClick={() => submit('draft')}
          disabled={!!submitting}
        >
          {submitting === 'draft' ? 'Saving…' : 'Save Draft'}
        </button>
        <button className={styles.cancelBtn} onClick={handleReset} disabled={!!submitting}>
          Cancel
        </button>
      </div>
    </div>
  );
});

GenerateInvoiceForm.displayName = 'GenerateInvoiceForm';
